import React, { useState, useEffect } from 'react';
import { useProjects } from '../../context/ProjectContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Plus, RefreshCw, Loader2, ExternalLink, GripVertical } from 'lucide-react';

const STATUS_COLORS = {
  'Prospective': 'bg-slate-100 border-slate-300 text-slate-700',
  'Applied': 'bg-blue-100 border-blue-300 text-blue-700',
  'Client Responded': 'bg-cyan-100 border-cyan-300 text-cyan-700',
  'Offered': 'bg-emerald-100 border-emerald-300 text-emerald-700',
  'On Hold': 'bg-amber-100 border-amber-300 text-amber-700',
  'Contract Started': 'bg-green-100 border-green-300 text-green-700',
  'Contract Ended': 'bg-purple-100 border-purple-300 text-purple-700',
  'Rejected': 'bg-red-100 border-red-300 text-red-700'
};

const KPI_COLORS = {
  'Prospective': 'border-l-slate-400',
  'Applied': 'border-l-blue-500',
  'Client Responded': 'border-l-cyan-500',
  'Offered': 'border-l-emerald-500',
  'On Hold': 'border-l-amber-500',
  'Contract Started': 'border-l-green-500',
  'Contract Ended': 'border-l-purple-500',
  'Rejected': 'border-l-red-500'
};

const ProjectsDashboard = ({ onNewProject, onViewProject }) => {
  const { getDashboardStats, updateProject, fetchProjects } = useProjects();
  const [stats, setStats] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draggedProject, setDraggedProject] = useState(null);

  const loadData = async () => {
    setLoading(true);
    const dashStats = await getDashboardStats();
    setStats(dashStats);
    const data = await fetchProjects({ page_size: 200 });
    setProjects(data?.projects || []);
    setLoading(false);
  };

  useEffect(() => { loadData(); }, []);

  const handleDragStart = (e, project) => {
    setDraggedProject(project);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDrop = async (e, newStatus) => {
    e.preventDefault();
    if (!draggedProject || draggedProject.status === newStatus) return;
    
    await updateProject(draggedProject.id, { status: newStatus });
    setDraggedProject(null);
    loadData();
  };

  const handleDragOver = (e) => { e.preventDefault(); };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;
  }

  const stages = stats?.stages || [];

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
        <Card className="border-l-4 border-l-blue-600" data-testid="kpi-total">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Total Projects</p>
            <p className="text-2xl font-bold text-slate-800">{stats?.total || 0}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-green-600" data-testid="kpi-converted">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Converted</p>
            <p className="text-2xl font-bold text-green-700">{stats?.converted || 0}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-cyan-500" data-testid="kpi-responded">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Responded</p>
            <p className="text-2xl font-bold text-cyan-700">{stats?.responded || 0}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-emerald-600" data-testid="kpi-conversion-rate">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Conversion %</p>
            <p className="text-2xl font-bold text-emerald-700">{stats?.conversion_rate || 0}%</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-purple-600" data-testid="kpi-response-rate">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Response Rate</p>
            <p className="text-2xl font-bold text-purple-700">{stats?.response_rate || 0}%</p>
          </CardContent>
        </Card>
      </div>

      {/* Action bar */}
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold text-slate-800">Pipeline Board</h2>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={loadData}><RefreshCw className="w-4 h-4 mr-1" />Refresh</Button>
          <Button size="sm" onClick={onNewProject} className="bg-blue-600 hover:bg-blue-700" data-testid="new-project-btn">
            <Plus className="w-4 h-4 mr-1" />New Project
          </Button>
        </div>
      </div>

      {/* Kanban Board */}
      <div className="flex gap-3 overflow-x-auto pb-4" data-testid="kanban-board">
        {stages.map(stage => {
          const stageProjects = projects.filter(p => p.status === stage);
          const colorClass = STATUS_COLORS[stage] || 'bg-gray-100 border-gray-300 text-gray-700';
          
          return (
            <div
              key={stage}
              className="flex-shrink-0 w-[220px]"
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, stage)}
              data-testid={`kanban-column-${stage.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className={`rounded-t-lg px-3 py-2 text-sm font-medium flex items-center justify-between ${colorClass}`}>
                <span className="truncate">{stage}</span>
                <Badge variant="secondary" className="ml-1 text-xs">{stageProjects.length}</Badge>
              </div>
              <div className="bg-slate-50 rounded-b-lg min-h-[200px] p-2 space-y-2 border border-t-0 border-slate-200">
                {stageProjects.map(project => (
                  <div
                    key={project.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, project)}
                    className="bg-white rounded-lg p-3 border border-slate-200 cursor-grab hover:shadow-md transition-shadow active:cursor-grabbing"
                    onClick={() => onViewProject(project.id)}
                    data-testid={`project-card-${project.id}`}
                  >
                    <p className="text-sm font-medium text-slate-800 line-clamp-2">{project.title}</p>
                    <div className="flex items-center gap-1 mt-2">
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0">{project.platform}</Badge>
                    </div>
                    <p className="text-[10px] text-slate-400 mt-1">{project.proposal_date}</p>
                    {project.tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {project.tags.slice(0, 2).map((t, i) => (
                          <Badge key={i} variant="outline" className="text-[9px] px-1 py-0 bg-blue-50 text-blue-600 border-blue-200">{t}</Badge>
                        ))}
                        {project.tags.length > 2 && <Badge variant="outline" className="text-[9px] px-1 py-0">+{project.tags.length - 2}</Badge>}
                      </div>
                    )}
                    {project.project_url && (
                      <a
                        href={project.project_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[10px] text-blue-500 hover:underline flex items-center gap-0.5 mt-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ExternalLink className="w-2.5 h-2.5" />View Posting
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ProjectsDashboard;
